import { memo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Settings } from 'lucide-react';
import { getTopNavItems, getActiveSectionFromPath } from '@/config/navigation';
import { useOrgContext } from '@/hooks/useOrgContext';
import { useCompanyContext } from '@/hooks/useCompanyContext';
import { useSuperadminContext } from '@/hooks/useSuperadminContext';
import { useNotifications } from '@/hooks/useNotifications';
import { cn } from '@/lib/utils';
import { ThemeToggleMemo } from '@/components/ui/theme-toggle';
import { UserMenu } from '@/components/layout/UserMenu';
import { NotificationBell } from '@/components/layout/NotificationBell';
import { NotificationsPanel } from '@/components/layout/NotificationsPanel';

function TopNavigation() {
  const location = useLocation();
  const { effectiveIndustry, isOrgOwner } = useOrgContext();
  const { isCompanyUser } = useCompanyContext();
  const { unreadCount } = useNotifications();
  const { isSuperadmin } = useSuperadminContext();
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const activeSection = getActiveSectionFromPath(location.pathname);
  const navItems = getTopNavItems(effectiveIndustry, isCompanyUser, isSuperadmin, isOrgOwner);

  return (
    <header className="h-14 bg-sidebar border-b border-sidebar-border flex items-center justify-between px-4 shrink-0">
      {/* Left: Logo + Nav Items */}
      <div className="flex items-center gap-1">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 mr-6">
          <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
            <span className="text-primary-foreground font-bold text-sm">W</span>
          </div>
          <span className="text-foreground font-semibold text-lg">Wingrow</span>
        </Link>

        {/* Nav Items */}
        <nav className="flex items-center gap-1">
          {navItems.map(item => {
            const Icon = item.icon;
            const isActive = activeSection === item.id;

            return (
              <Link
                key={item.id}
                to={item.path}
                className={cn(
                  'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-sidebar-primary text-sidebar-primary-foreground'
                    : 'text-foreground-muted hover:text-foreground hover:bg-sidebar-accent'
                )}
              >
                <Icon className="w-4 h-4" />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>
      </div>

      {/* Right: Theme Toggle + Notifications + Settings + User */}
      <div className="flex items-center gap-2">
        <ThemeToggleMemo />

        <NotificationBell unreadCount={unreadCount} onClick={() => setNotificationsOpen(true)} />

        <Link
          to="/business-settings"
          className="p-2 rounded-lg text-foreground-muted hover:text-foreground hover:bg-sidebar-accent transition-colors"
          title="Settings"
        >
          <Settings className="w-5 h-5" />
        </Link>

        <UserMenu />
      </div>

      <NotificationsPanel open={notificationsOpen} onOpenChange={setNotificationsOpen} />
    </header>
  );
}

export const TopNavigationMemo = memo(TopNavigation);
